import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Trash2, Clock, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import TimeEntryCard from '../components/TimeEntryCard';
import TimeEntryEditModal from '../components/TimeEntryEditModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, thisWeek: 0 });
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);

  useEffect(() => {
    fetchProjectAndEntries();
  }, [id]);

  const fetchProjectAndEntries = async () => {
    try {
      const [projectRes, entriesRes] = await Promise.all([
        axios.get(`${API}/projects/${id}`),
        axios.get(`${API}/time-entries`, { params: { project_id: id } })
      ]);
      
      setProject(projectRes.data);
      setEntries(entriesRes.data);
      
      // Calculate stats
      const total = entriesRes.data.reduce((sum, e) => sum + e.duration_minutes, 0);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      const thisWeek = entriesRes.data
        .filter(e => new Date(e.date) >= weekAgo)
        .reduce((sum, e) => sum + e.duration_minutes, 0);
      
      setStats({ total, thisWeek });
    } catch (error) {
      console.error('Failed to fetch project:', error);
      toast.error('Failed to load project');
      navigate('/projects');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (entryId) => {
    try {
      await axios.delete(`${API}/time-entries/${entryId}`);
      toast.success('Entry deleted');
      fetchProjectAndEntries();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const handleEdit = (entry) => {
    setSelectedEntry(entry);
    setEditModalOpen(true);
  };

  const handleEditComplete = () => {
    setEditModalOpen(false);
    setSelectedEntry(null);
    fetchProjectAndEntries();
    toast.success('Entry updated successfully!');
  };

  const handleDeleteProject = async () => {
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.delete(`${API}/projects/${id}`);
      toast.success('Project deleted');
      navigate('/projects');
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 50%, #80deea 100%)' }}>
        <div className="inline-block w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-8" style={{ background: 'linear-gradient(135deg, #e0f7fa 0%, #b2ebf2 50%, #80deea 100%)' }}>
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md shadow-md">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/projects')}
                variant="ghost"
                size="icon"
                data-testid="back-button"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center">
                <div
                  className="w-4 h-12 rounded-full mr-4"
                  style={{ backgroundColor: project?.color }}
                />
                <h1 className="text-3xl font-bold text-gray-800" style={{ fontFamily: 'Space Grotesk' }} data-testid="project-name">
                  {project?.name}
                </h1>
              </div>
            </div>
            <Button
              onClick={handleDeleteProject}
              variant="destructive"
              size="icon"
              data-testid="delete-project-button"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0" data-testid="total-hours-card">
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-gray-600 text-sm">Total Hours</p>
                <Clock className="w-5 h-5 text-gray-400" />
              </div>
              <p className="text-3xl font-bold text-gray-800" style={{ fontFamily: 'Space Grotesk' }}>
                {Math.floor(stats.total / 60)}h {stats.total % 60}m
              </p>
            </div>
          </Card>
          <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0" data-testid="week-hours-card">
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-gray-600 text-sm">This Week</p>
                <TrendingUp className="w-5 h-5 text-gray-400" />
              </div>
              <p className="text-3xl font-bold text-gray-800" style={{ fontFamily: 'Space Grotesk' }}>
                {Math.floor(stats.thisWeek / 60)}h {stats.thisWeek % 60}m
              </p>
            </div>
          </Card>
          <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0" data-testid="entries-count-card">
            <div className="p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-gray-600 text-sm">Total Entries</p>
              </div>
              <p className="text-3xl font-bold text-gray-800" style={{ fontFamily: 'Space Grotesk' }}>
                {entries.length}
              </p>
            </div>
          </Card>
        </div>

        {/* Entries List */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold text-gray-800" style={{ fontFamily: 'Space Grotesk' }}>Time Entries</h2>
          {entries.length === 0 ? (
            <Card className="bg-white/80 backdrop-blur-sm shadow-lg border-0" data-testid="no-entries">
              <div className="p-12 text-center">
                <p className="text-gray-600">No time entries for this project yet</p>
              </div>
            </Card>
          ) : (
            entries.map(entry => (
              <TimeEntryCard
                key={entry.id}
                entry={entry}
                onDelete={handleDelete}
                onEdit={handleEdit}
              />
            ))
          )}
        </div>
      </div>

      {/* Edit Modal */}
      <TimeEntryEditModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        onComplete={handleEditComplete}
        entry={selectedEntry}
        prefilledData={null}
      />
    </div>
  );
};

export default ProjectDetail;
